import type { SpreadsheetRow, SpreadsheetTable, SpreadsheetWorkbook } from "../../domain/import/spreadsheet.js";

/**
 * LEITOR DE PDF PESQUISÁVEL (Bloco 04, complemento).
 *
 * Extrai o texto com as coordenadas de cada fragmento e remonta as tabelas,
 * devolvendo exatamente o mesmo `SpreadsheetWorkbook` que o leitor de XLSX
 * produz. A partir daí o modelo intermediário, a validação, o staging e a
 * persistência são os mesmos — não existe segunda regra de negócio para PDF.
 *
 * O que é tratado:
 * - Várias páginas, com a tabela continuando de uma para a outra.
 * - Cabeçalho repetido no topo de cada página: reconhecido e descartado.
 * - Capa, cabeçalho institucional, rodapé e paginação: descartados por
 *   repetição na mesma faixa vertical, não por posição fixa arbitrária.
 * - Cada tabela vira uma "aba" com o nome do título que a antecede, para
 *   que `findSheetName(wb, "Sistemas")` funcione igual ao XLSX.
 *
 * O que NÃO é tratado aqui: PDF digitalizado sem camada de texto. Nesse caso
 * `readPdfAsWorkbook` lança `PdfNoTextLayerError`, e a decisão de recorrer a
 * OCR é de quem chama.
 */

export class PdfNoTextLayerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PdfNoTextLayerError";
  }
}

export class PdfFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PdfFormatError";
  }
}

/** Um fragmento de texto com a posição em que foi desenhado. */
export interface PdfTextItem {
  readonly page: number;
  readonly x: number;
  readonly y: number;
  readonly text: string;
  /** Tamanho efetivo da fonte — usado para estimar largura e reconhecer títulos. */
  readonly size: number;
}

// ------------------------------------------------------------------ inflate

async function inflate(data: Uint8Array): Promise<Uint8Array | null> {
  // PDF usa zlib com cabeçalho; "deflate" cobre isso. Alguns geradores
  // emitem fluxo cru, então há uma segunda tentativa com "deflate-raw".
  // Devolve null em vez de lançar: um fluxo ilegível (uma fonte, por
  // exemplo) não pode derrubar a leitura do documento inteiro.
  for (const format of ["deflate", "deflate-raw"] as const) {
    try {
      const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream(format));
      return new Uint8Array(await new Response(stream).arrayBuffer());
    } catch { /* tenta o próximo formato */ }
  }
  return null;
}

// -------------------------------------------------------------- objetos PDF

interface PdfObject {
  readonly num: number;
  readonly dict: string;
  readonly streamStart: number;
  readonly streamEnd: number;
}

const LATIN = (bytes: Uint8Array, from: number, to: number): string => {
  let out = "";
  const end = Math.min(to, bytes.length);
  for (let i = from; i < end; i++) out += String.fromCharCode(bytes[i]!);
  return out;
};

/**
 * Percorre os objetos do PDF. O tamanho do fluxo vem de `/Length`, não de
 * uma busca por "endstream": dados binários comprimidos contêm qualquer
 * sequência de bytes, inclusive palavras-chave do PDF, e procurá-las
 * desalinharia a leitura.
 */
function parseObjects(bytes: Uint8Array): Map<number, PdfObject> {
  const text = LATIN(bytes, 0, bytes.length);
  const objects = new Map<number, PdfObject>();
  const re = /(\d+)\s+\d+\s+obj\b/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const num = Number(match[1]);
    const bodyStart = match.index + match[0].length;

    const endObjIndex = text.indexOf("endobj", bodyStart);
    const streamIndex = text.indexOf("stream", bodyStart);
    const hasStream = streamIndex >= 0 && (endObjIndex < 0 || streamIndex < endObjIndex);

    if (!hasStream) {
      if (endObjIndex < 0) continue;
      objects.set(num, { num, dict: text.slice(bodyStart, endObjIndex), streamStart: -1, streamEnd: -1 });
      re.lastIndex = endObjIndex;
      continue;
    }

    const dict = text.slice(bodyStart, streamIndex);
    let cursor = streamIndex + "stream".length;
    if (text[cursor] === "\r") cursor++;
    if (text[cursor] === "\n") cursor++;

    const lengthMatch = /\/Length\s+(\d+)(?!\s+\d+\s+R)/.exec(dict);
    let streamEnd: number;
    if (lengthMatch) {
      streamEnd = cursor + Number(lengthMatch[1]);
    } else {
      // /Length indireto: recorre a "endstream" e apara o fim de linha.
      const endStream = text.indexOf("endstream", cursor);
      streamEnd = endStream < 0 ? cursor : endStream;
      while (streamEnd > cursor && (text[streamEnd - 1] === "\n" || text[streamEnd - 1] === "\r")) streamEnd--;
    }

    objects.set(num, { num, dict, streamStart: cursor, streamEnd });
    const after = text.indexOf("endobj", streamEnd);
    re.lastIndex = after < 0 ? streamEnd : after;
  }
  if (objects.size === 0) throw new PdfFormatError("O arquivo não é um PDF válido (nenhum objeto encontrado).");
  return objects;
}

async function streamOf(bytes: Uint8Array, object: PdfObject): Promise<Uint8Array> {
  if (object.streamStart < 0) return new Uint8Array();
  const raw = bytes.subarray(object.streamStart, object.streamEnd);
  if (/\/Filter\s*\/FlateDecode/.test(object.dict) || /\/Filter\s*\[\s*\/FlateDecode/.test(object.dict)) {
    return (await inflate(raw)) ?? new Uint8Array();
  }
  return raw;
}

// ------------------------------------------------------------- ToUnicode

/** Mapa código→texto de uma fonte, lido do CMap /ToUnicode. */
type ToUnicode = Map<number, string>;

/**
 * Converte o destino hexadecimal de um CMap em texto.
 *
 * O valor pode conter VÁRIAS unidades UTF-16, inclusive separadas por espaço
 * (`<0066 006c>` é a ligadura "fl"). Ignorar essas entradas desalinha todo o
 * restante do mapa e o texto sai embaralhado, então o espaço é removido
 * antes da conversão.
 */
function hexToString(hex: string): string {
  const clean = hex.replace(/\s+/g, "");
  let out = "";
  for (let i = 0; i + 4 <= clean.length; i += 4) {
    const code = parseInt(clean.slice(i, i + 4), 16);
    if (!Number.isNaN(code)) out += String.fromCodePoint(code);
  }
  return out;
}

function parseToUnicode(cmap: string): ToUnicode {
  const map: ToUnicode = new Map();
  const charRe = /beginbfchar([\s\S]*?)endbfchar/g;
  let block: RegExpExecArray | null;
  while ((block = charRe.exec(cmap)) !== null) {
    const pairRe = /<([0-9A-Fa-f\s]+)>\s*<([0-9A-Fa-f\s]+)>/g;
    let pair: RegExpExecArray | null;
    while ((pair = pairRe.exec(block[1] ?? "")) !== null) {
      map.set(parseInt(pair[1]!.replace(/\s+/g, ""), 16), hexToString(pair[2]!));
    }
  }
  const rangeRe = /beginbfrange([\s\S]*?)endbfrange/g;
  while ((block = rangeRe.exec(cmap)) !== null) {
    const body = block[1] ?? "";
    // Duas formas: "<de> <ate> <base>" (deslocamento) e
    // "<de> <ate> [<v1> <v2> ...]" (lista explícita, usada pelo pdfmake).
    const entryRe = /<([0-9A-Fa-f\s]+?)>\s*<([0-9A-Fa-f\s]+?)>\s*(\[[\s\S]*?\]|<[0-9A-Fa-f\s]+?>)/g;
    let range: RegExpExecArray | null;
    while ((range = entryRe.exec(body)) !== null) {
      const from = parseInt(range[1]!.replace(/\s+/g, ""), 16);
      const to = parseInt(range[2]!.replace(/\s+/g, ""), 16);
      const target = range[3]!;

      if (target.startsWith("[")) {
        const values = [...target.matchAll(/<([0-9A-Fa-f\s]+?)>/g)].map(m => m[1]!);
        for (let offset = 0; offset < values.length && from + offset <= to; offset++) {
          map.set(from + offset, hexToString(values[offset]!));
        }
        continue;
      }

      const base = parseInt(target.slice(1, -1).replace(/s+/g, ""), 16);
      for (let code = from; code <= to && code - from < 65536; code++) {
        map.set(code, String.fromCodePoint(base + (code - from)));
      }
    }
  }
  return map;
}

// ----------------------------------------------------- conteúdo da página

interface TextState {
  matrix: [number, number, number, number, number, number];
  line: [number, number, number, number, number, number];
  leading: number;
  font: string;
  size: number;
}

function multiply(
  a: [number, number, number, number, number, number],
  b: [number, number, number, number, number, number]
): [number, number, number, number, number, number] {
  return [
    a[0] * b[0] + a[1] * b[2],
    a[0] * b[1] + a[1] * b[3],
    a[2] * b[0] + a[3] * b[2],
    a[2] * b[1] + a[3] * b[3],
    a[4] * b[0] + a[5] * b[2] + b[4],
    a[4] * b[1] + a[5] * b[3] + b[5]
  ];
}

/** Lê uma string literal do fluxo, respeitando escapes e parênteses aninhados. */
function readLiteral(source: string, start: number): { value: number[]; next: number } {
  const codes: number[] = [];
  let depth = 1;
  let i = start;
  while (i < source.length) {
    const ch = source[i]!;
    if (ch === "\\") {
      const escaped = source[i + 1]!;
      const octal = /^[0-7]{1,3}/.exec(source.slice(i + 1, i + 4));
      if (octal) {
        codes.push(parseInt(octal[0], 8));
        i += 1 + octal[0].length;
        continue;
      }
      const table: Record<string, number> = { n: 10, r: 13, t: 9, b: 8, f: 12 };
      codes.push(table[escaped] ?? escaped.charCodeAt(0));
      i += 2;
      continue;
    }
    if (ch === "(") { depth++; codes.push(40); i++; continue; }
    if (ch === ")") {
      depth--;
      if (depth === 0) return { value: codes, next: i + 1 };
      codes.push(41); i++; continue;
    }
    codes.push(ch.charCodeAt(0));
    i++;
  }
  return { value: codes, next: i };
}

function decodeCodes(codes: readonly number[], toUnicode: ToUnicode | undefined, twoByte: boolean): string {
  if (!toUnicode || toUnicode.size === 0) {
    return codes.map(code => String.fromCharCode(code)).join("");
  }
  let out = "";
  if (twoByte) {
    for (let i = 0; i + 1 < codes.length; i += 2) {
      const code = (codes[i]! << 8) | codes[i + 1]!;
      out += toUnicode.get(code) ?? "";
    }
    if (out !== "") return out;
  }
  for (const code of codes) out += toUnicode.get(code) ?? String.fromCharCode(code);
  return out;
}

function extractPageText(content: string, page: number, fonts: Map<string, ToUnicode>): PdfTextItem[] {
  const items: PdfTextItem[] = [];
  const state: TextState = { matrix: [1, 0, 0, 1, 0, 0], line: [1, 0, 0, 1, 0, 0], leading: 0, font: "", size: 0 };
  const stack: number[] = [];
  let i = 0;

  const push = (codes: number[]) => {
    const toUnicode = fonts.get(state.font);
    const twoByte = Boolean(toUnicode && toUnicode.size > 0);
    const text = decodeCodes(codes, toUnicode, twoByte);
    if (text.trim() === "") return;
    const scale = Math.abs(state.matrix[0]) || 1;
    items.push({ page, x: state.matrix[4], y: state.matrix[5], text, size: state.size * scale });
  };

  let pendingName = "";

  while (i < content.length) {
    const ch = content[i]!;

    // Nome de recurso (/F1, /F2...). É ele que diz qual fonte — e portanto
    // qual mapa ToUnicode — decodifica os próximos textos. Sem rastreá-lo,
    // o texto em negrito sai decodificado com o mapa da fonte normal.
    if (ch === "/") {
      const name = /^\/([^\s/[\]<>()]+)/.exec(content.slice(i));
      if (name) {
        pendingName = name[1]!;
        i += name[0].length;
        continue;
      }
    }

    if (ch === "(") {
      const literal = readLiteral(content, i + 1);
      // A string pode pertencer a Tj, TJ, ' ou ". O operador vem depois.
      const after = content.slice(literal.next, literal.next + 40);
      if (/^\s*(Tj|TJ|'|")/.test(after) || /^\s*[-\d.]/.test(after)) push(literal.value);
      i = literal.next;
      continue;
    }

    if (ch === "<" && content[i + 1] !== "<") {
      const close = content.indexOf(">", i);
      if (close > 0) {
        const hex = content.slice(i + 1, close).replace(/\s/g, "");
        const codes: number[] = [];
        for (let h = 0; h + 1 < hex.length; h += 2) codes.push(parseInt(hex.slice(h, h + 2), 16));
        push(codes);
        i = close + 1;
        continue;
      }
    }

    const token = /^[A-Za-z'"*]+|^-?[\d.]+/.exec(content.slice(i));
    if (!token) { i++; continue; }
    const value = token[0];

    if (/^-?[\d.]+$/.test(value)) {
      stack.push(Number(value));
      i += value.length;
      continue;
    }

    switch (value) {
      case "BT":
        state.matrix = [1, 0, 0, 1, 0, 0];
        state.line = [1, 0, 0, 1, 0, 0];
        break;
      case "Tf": {
        const size = stack.pop();
        state.size = typeof size === "number" ? size : state.size;
        if (pendingName) state.font = pendingName;
        break;
      }
      case "TL":
        state.leading = stack.pop() ?? state.leading;
        break;
      case "Td": {
        const ty = stack.pop() ?? 0;
        const tx = stack.pop() ?? 0;
        state.line = multiply([1, 0, 0, 1, tx, ty], state.line);
        state.matrix = [...state.line] as TextState["matrix"];
        break;
      }
      case "TD": {
        const ty = stack.pop() ?? 0;
        const tx = stack.pop() ?? 0;
        state.leading = -ty;
        state.line = multiply([1, 0, 0, 1, tx, ty], state.line);
        state.matrix = [...state.line] as TextState["matrix"];
        break;
      }
      case "Tm": {
        const f = stack.pop() ?? 0, e = stack.pop() ?? 0, d = stack.pop() ?? 1;
        const c = stack.pop() ?? 0, b = stack.pop() ?? 0, a = stack.pop() ?? 1;
        state.line = [a, b, c, d, e, f];
        state.matrix = [a, b, c, d, e, f];
        break;
      }
      case "T*":
        state.line = multiply([1, 0, 0, 1, 0, -state.leading], state.line);
        state.matrix = [...state.line] as TextState["matrix"];
        break;
      default:
        break;
    }
    if (value !== "Tf") stack.length = 0;
    i += value.length;
  }

  return items;
}

// ----------------------------------------------------- remontagem da tabela

const ROW_TOLERANCE = 3;

interface PdfRow {
  readonly page: number;
  readonly y: number;
  readonly items: PdfTextItem[];
}

function groupIntoRows(items: readonly PdfTextItem[]): PdfRow[] {
  const byPage = new Map<number, PdfTextItem[]>();
  for (const item of items) {
    const list = byPage.get(item.page) ?? [];
    list.push(item);
    byPage.set(item.page, list);
  }
  const rows: PdfRow[] = [];
  for (const page of [...byPage.keys()].sort((a, b) => a - b)) {
    const pageItems = byPage.get(page)!.slice().sort((a, b) => b.y - a.y || a.x - b.x);
    let current: PdfTextItem[] = [];
    let currentY = Number.NaN;
    for (const item of pageItems) {
      if (current.length === 0 || Math.abs(item.y - currentY) <= ROW_TOLERANCE) {
        current.push(item);
        currentY = Number.isNaN(currentY) ? item.y : currentY;
      } else {
        rows.push({ page, y: currentY, items: current.slice().sort((a, b) => a.x - b.x) });
        current = [item];
        currentY = item.y;
      }
    }
    if (current.length > 0) rows.push({ page, y: currentY, items: current.slice().sort((a, b) => a.x - b.x) });
  }
  return rows.map(row => ({ ...row, items: mergeFragments(row.items) }));
}

/**
 * O gerador de PDF quebra uma mesma célula em vários fragmentos ("T", "A",
 * "G", "do", "sistema"). Sem juntá-los, o cabeçalho viraria dezenas de
 * colunas. Como o PDF não declara a largura do texto, ela é estimada pelo
 * tamanho da fonte: fragmentos cuja folga é menor que um espaço pertencem à
 * mesma célula.
 */
function mergeFragments(items: readonly PdfTextItem[]): PdfTextItem[] {
  if (items.length === 0) return [];
  const merged: PdfTextItem[] = [];
  let current = { ...items[0]! };
  let currentWidth = estimateWidth(current);

  for (let i = 1; i < items.length; i++) {
    const next = items[i]!;
    const gap = next.x - (current.x + currentWidth);
    const spaceWidth = Math.max(next.size, current.size) * 0.55;
    if (gap < spaceWidth) {
      const needsSpace = gap > Math.max(next.size, current.size) * 0.12;
      current = { ...current, text: current.text + (needsSpace ? " " : "") + next.text };
      currentWidth = (next.x + estimateWidth(next)) - current.x;
    } else {
      merged.push(current);
      current = { ...next };
      currentWidth = estimateWidth(next);
    }
  }
  merged.push(current);
  return merged;
}

/** Largura aproximada do texto. Não precisa ser exata: só separa célula de célula. */
function estimateWidth(item: PdfTextItem): number {
  const size = item.size > 0 ? item.size : 8;
  return item.text.length * size * 0.5;
}

const rowText = (row: PdfRow): string => row.items.map(i => i.text.trim()).join("");

/**
 * Descarta mobiliário de página: linhas cujo texto se repete em quase todas
 * as páginas na mesma faixa vertical (cabeçalho institucional, rodapé) e
 * numeração de página. O cabeçalho da TABELA também se repete, por isso a
 * exclusão exige que a linha esteja perto da borda superior ou inferior.
 */
const PAGE_NUMBER = /^\s*(p[áa]gina\s*)?\d+\s*(de\s*\d+)?\s*$/i;

function stripPageFurniture(rows: readonly PdfRow[]): PdfRow[] {
  const pages = new Set(rows.map(r => r.page));

  // Quantas páginas repetem cada texto isolado. A contagem é por FRAGMENTO,
  // não por linha: o rodapé costuma trazer o número da página junto, o que
  // faria a linha inteira parecer diferente a cada página.
  const itemPages = new Map<string, Set<number>>();
  for (const row of rows) {
    for (const item of row.items) {
      const key = item.text.trim();
      const set = itemPages.get(key) ?? new Set<number>();
      set.add(row.page);
      itemPages.set(key, set);
    }
  }

  const repeatedEverywhere = (text: string): boolean =>
    pages.size >= 2 && (itemPages.get(text)?.size ?? 0) >= pages.size * 0.6;

  const out: PdfRow[] = [];
  for (const row of rows) {
    // Uma linha larga é cabeçalho de tabela, não mobiliário — mesmo que se
    // repita em todas as páginas. Só linhas curtas entram nessa filtragem.
    if (row.items.length > 4) { out.push(row); continue; }

    const kept = row.items.filter(item => {
      const text = item.text.trim();
      if (PAGE_NUMBER.test(text)) return false;
      return !repeatedEverywhere(text);
    });
    if (kept.length > 0) out.push({ ...row, items: kept });
  }
  return out;
}

interface DetectedTable {
  title: string;
  headerRow: PdfRow;
  dataRows: PdfRow[];
}

/**
 * Título de seção: linha de um só texto, curto, desenhado em corpo maior que
 * o da tabela. O tamanho é o que distingue um título de uma célula que ficou
 * sozinha na linha — sem isso, qualquer célula isolada partiria a tabela.
 */
function isTitleRow(row: PdfRow, bodySize: number): boolean {
  if (row.items.length !== 1) return false;
  const item = row.items[0]!;
  const text = item.text.trim();
  if (text.length === 0 || text.length > 60) return false;
  if (!/[A-Za-zÀ-ÿ]/.test(text)) return false;
  return item.size >= bodySize * 1.25;
}

/** Corpo do documento: o tamanho de fonte mais frequente, que é o da tabela. */
function bodyFontSize(rows: readonly PdfRow[]): number {
  const counts = new Map<number, number>();
  for (const row of rows) {
    for (const item of row.items) {
      const key = Math.round(item.size * 2) / 2;
      counts.set(key, (counts.get(key) ?? 0) + item.text.length);
    }
  }
  let best = 8;
  let bestCount = -1;
  for (const [size, count] of counts) {
    if (count > bestCount) { bestCount = count; best = size; }
  }
  return best;
}

function detectTables(rows: readonly PdfRow[]): DetectedTable[] {
  const tables: DetectedTable[] = [];
  const bodySize = bodyFontSize(rows);
  const headerYByPage = new Map<number, number>();
  let pendingTitle = "";
  let current: DetectedTable | null = null;

  for (const row of rows) {
    const columns = row.items.length;

    if (isTitleRow(row, bodySize)) {
      // Um título encerra a tabela anterior e passa a valer para a próxima.
      if (current) { tables.push(current); current = null; }
      pendingTitle = row.items[0]!.text.trim();
      continue;
    }

    if (columns < 2) continue;

    if (!current) {
      if (columns < 3) continue; // ruído solto, não é cabeçalho de tabela
      current = { title: pendingTitle || `Tabela ${tables.length + 1}`, headerRow: row, dataRows: [] };
      headerYByPage.set(row.page, row.y);
      continue;
    }

    // Cabeçalho repetido no topo da página seguinte: mesma sequência de textos.
    if (rowText(row) === rowText(current.headerRow)) {
      headerYByPage.set(row.page, row.y);
      continue;
    }

    // Numa página, o dado vem ABAIXO do cabeçalho. O que está acima dele é
    // cabeçalho institucional da página, não registro.
    const headerY = headerYByPage.get(row.page);
    if (headerY !== undefined && row.y > headerY) continue;

    current.dataRows.push(row);
  }
  if (current) tables.push(current);
  return tables;
}

/** Converte a tabela detectada no mesmo formato do XLSX, alinhando por coluna. */
function toSpreadsheetTable(table: DetectedTable, sheetName: string): SpreadsheetTable {
  const headers = table.headerRow.items.map(item => item.text.trim());
  const centers = table.headerRow.items.map(item => item.x);

  const nearestColumn = (x: number): number => {
    let best = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let c = 0; c < centers.length; c++) {
      const distance = Math.abs(centers[c]! - x);
      if (distance < bestDistance) { bestDistance = distance; best = c; }
    }
    return best;
  };

  const firstHeader = headers.findIndex(h => h !== "");
  const namedColumns = headers.filter(h => h !== "").length;
  // Um registro preenche boa parte das colunas. Uma linha que começa na
  // primeira coluna mas preenche muito pouco é mobiliário que sobrou
  // (cabeçalho institucional, nota de rodapé), não um registro.
  const minimumFilled = Math.max(3, Math.ceil(namedColumns * 0.3));
  const rows: Array<{ rowNumber: number; values: Record<string, string> }> = [];

  table.dataRows.forEach((row, index) => {
    const cells = new Array<string>(headers.length).fill("");
    for (const item of row.items) {
      const column = nearestColumn(item.x);
      const text = item.text.trim();
      cells[column] = cells[column] ? `${cells[column]} ${text}` : text;
    }
    const values: Record<string, string> = {};
    let filled = 0;
    headers.forEach((header, column) => {
      if (!header) return;
      const value = cells[column] ?? "";
      values[header] = value;
      if (value !== "") filled++;
    });
    if (filled === 0) return;

    // Continuação: quando uma célula quebra em duas linhas visuais, a linha
    // seguinte vem sem a primeira coluna. Ela pertence ao registro anterior,
    // não é um novo registro.
    const previous = rows[rows.length - 1];
    const startsRecord = firstHeader >= 0 && (cells[firstHeader] ?? "") !== "";
    if (!startsRecord && previous) {
      for (const header of headers) {
        if (!header) continue;
        const extra = values[header] ?? "";
        if (extra === "") continue;
        previous.values[header] = previous.values[header] ? `${previous.values[header]} ${extra}` : extra;
      }
      return;
    }

    if (filled < minimumFilled) return;
    rows.push({ rowNumber: index + 2, values });
  });

  return { sheetName, headers, headerRowNumber: 1, rows: rows as readonly SpreadsheetRow[] };
}

// --------------------------------------------------------------- entrada

/**
 * Lê um PDF pesquisável e devolve as tabelas como abas.
 * Lança `PdfNoTextLayerError` quando não há camada de texto utilizável.
 */
export async function readPdfAsWorkbook(bytes: Uint8Array): Promise<SpreadsheetWorkbook> {
  const header = LATIN(bytes, 0, 5);
  if (!header.startsWith("%PDF")) throw new PdfFormatError("O arquivo não é um PDF válido.");

  const objects = parseObjects(bytes);

  // Fontes com ToUnicode, indexadas pelo nome usado no recurso da página.
  const fonts = new Map<string, ToUnicode>();
  for (const object of objects.values()) {
    if (!/\/Type\s*\/Font/.test(object.dict)) continue;
    const toUnicodeRef = /\/ToUnicode\s+(\d+)\s+\d+\s+R/.exec(object.dict);
    if (!toUnicodeRef) continue;
    const cmapObject = objects.get(Number(toUnicodeRef[1]));
    if (!cmapObject) continue;
    const cmap = new TextDecoder("latin1").decode(await streamOf(bytes, cmapObject));
    const parsed = parseToUnicode(cmap);
    // O nome do recurso é resolvido depois; aqui guardamos por número do objeto.
    fonts.set(`obj:${object.num}`, parsed);
  }

  const pageObjects = [...objects.values()].filter(o => /\/Type\s*\/Page\b/.test(o.dict));
  if (pageObjects.length === 0) throw new PdfFormatError("O PDF não tem páginas legíveis.");

  const items: PdfTextItem[] = [];
  let pageNumber = 0;
  for (const page of pageObjects) {
    pageNumber++;

    // Mapeia /F1 -> ToUnicode da fonte referenciada no recurso desta página.
    const pageFonts = new Map<string, ToUnicode>();
    const fontResource = /\/Font\s*<<([\s\S]*?)>>/.exec(page.dict);
    let fontBlock = fontResource?.[1] ?? "";
    if (!fontBlock) {
      const resourceRef = /\/Resources\s+(\d+)\s+\d+\s+R/.exec(page.dict);
      if (resourceRef) {
        const resource = objects.get(Number(resourceRef[1]));
        fontBlock = resource ? (/\/Font\s*<<([\s\S]*?)>>/.exec(resource.dict)?.[1] ?? "") : "";
      }
    }
    const fontRefRe = /\/([^\s/]+)\s+(\d+)\s+\d+\s+R/g;
    let fontRef: RegExpExecArray | null;
    while ((fontRef = fontRefRe.exec(fontBlock)) !== null) {
      const mapped = fonts.get(`obj:${Number(fontRef[2])}`);
      if (mapped) pageFonts.set(fontRef[1]!, mapped);
    }

    const contentRefs: number[] = [];
    const single = /\/Contents\s+(\d+)\s+\d+\s+R/.exec(page.dict);
    if (single) contentRefs.push(Number(single[1]));
    const array = /\/Contents\s*\[([^\]]*)\]/.exec(page.dict);
    if (array) {
      const refRe = /(\d+)\s+\d+\s+R/g;
      let ref: RegExpExecArray | null;
      while ((ref = refRe.exec(array[1] ?? "")) !== null) contentRefs.push(Number(ref[1]));
    }

    for (const ref of contentRefs) {
      const contentObject = objects.get(ref);
      if (!contentObject) continue;
      const content = new TextDecoder("latin1").decode(await streamOf(bytes, contentObject));
      items.push(...extractPageText(content, pageNumber, pageFonts));
    }
  }

  const meaningful = items.filter(item => /[A-Za-zÀ-ÿ0-9]/.test(item.text));
  if (meaningful.length < 20) {
    throw new PdfNoTextLayerError(
      "Este PDF não tem camada de texto utilizável (provavelmente digitalizado). É necessário OCR para importá-lo."
    );
  }

  const rows = stripPageFurniture(groupIntoRows(meaningful));
  const detected = detectTables(rows);

  const tables = new Map<string, SpreadsheetTable>();
  const names: string[] = [];
  for (const table of detected) {
    let name = table.title;
    let suffix = 2;
    while (tables.has(name)) name = `${table.title} (${suffix++})`;
    tables.set(name, toSpreadsheetTable(table, name));
    names.push(name);
  }

  return { sheetNames: names, table: (sheetName: string) => tables.get(sheetName) ?? null };
}

/** Quantas páginas e quantos fragmentos de texto o PDF tem — usado em diagnóstico e teste. */
export async function inspectPdf(bytes: Uint8Array): Promise<{ pages: number; textItems: number }> {
  const objects = parseObjects(bytes);
  const pages = [...objects.values()].filter(o => /\/Type\s*\/Page\b/.test(o.dict)).length;
  let textItems = 0;
  for (const object of objects.values()) {
    if (object.streamStart < 0) continue;
    const content = new TextDecoder("latin1").decode(await streamOf(bytes, object));
    textItems += (content.match(/\bTj\b|\bTJ\b/g) ?? []).length;
  }
  return { pages, textItems };
}
